import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  Shield as ShieldIcon,
  Block as BlockIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  VpnKey as KeyIcon,
  Share as ShareIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface SecurityEvent {
  id: string;
  type: 'failed_verification' | 'suspicious_sharing' | 'multiple_attempts' | 'unusual_location' | 'rate_limit_hit' | 'expired_code_attempt';
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  user: {
    id: string;
    name: string;
    role: 'renter' | 'driver' | 'host';
    phone: string;
  };
  details: {
    action: string;
    resource: string;
    location?: string;
    ipAddress: string;
    userAgent: string;
    attempts?: number;
    maxAttempts?: number;
  };
  bookingId?: string;
  resolved: boolean;
  resolvedBy?: string;
  resolvedAt?: string;
}

interface SecurityMetrics {
  totalEvents: number;
  criticalEvents: number;
  resolvedEvents: number;
  averageResponseTime: string;
  topThreatType: string;
  securityScore: number;
  trends: {
    eventsChange: number;
    scoreChange: number;
    responseTimeChange: number;
  };
}

const SecurityMonitoringPage: React.FC = () => {
  const theme = useTheme();
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [metrics, setMetrics] = useState<SecurityMetrics>({
    totalEvents: 47,
    criticalEvents: 3,
    resolvedEvents: 39,
    averageResponseTime: '3.2m',
    topThreatType: 'Failed Verifications',
    securityScore: 92,
    trends: {
      eventsChange: -12,
      scoreChange: 5,
      responseTimeChange: -8,
    },
  });

  useEffect(() => {
    fetchSecurityEvents();
    const interval = setInterval(fetchSecurityEvents, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSecurityEvents = async () => {
    // Mock data - replace with actual API call
    const mockEvents: SecurityEvent[] = [
      {
        id: '1',
        type: 'failed_verification',
        severity: 'high',
        timestamp: '2024-01-20T14:30:00Z',
        user: { id: 'u1', name: 'John Doe', role: 'renter', phone: '+234***4567' },
        details: {
          action: 'Access code verification',
          resource: 'Vehicle pickup code 847392',
          location: 'Victoria Island, Lagos',
          ipAddress: '197.149.***.**',
          userAgent: 'PolarRide Mobile/2.1.0 (iOS 17.2)',
          attempts: 3,
          maxAttempts: 3,
        },
        bookingId: 'bk_789',
        resolved: false,
      },
      {
        id: '2',
        type: 'suspicious_sharing',
        severity: 'critical',
        timestamp: '2024-01-20T14:15:00Z',
        user: { id: 'u2', name: 'Sarah Johnson', role: 'renter', phone: '+234***7890' },
        details: {
          action: 'Trip sharing access',
          resource: 'Trip share sh_abc123',
          location: 'Unknown (VPN detected)',
          ipAddress: '185.220.***.**',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
        },
        bookingId: 'bk_456',
        resolved: false,
      },
      {
        id: '3',
        type: 'multiple_attempts',
        severity: 'medium',
        timestamp: '2024-01-20T13:45:00Z',
        user: { id: 'd1', name: 'Michael Brown', role: 'driver', phone: '+234***1234' },
        details: {
          action: 'Driver code verification',
          resource: 'Driver verification code 293847',
          location: 'Lekki, Lagos',
          ipAddress: '197.149.***.**',
          userAgent: 'PolarRide Mobile/2.0.8 (Android 13)',
          attempts: 2,
          maxAttempts: 3,
        },
        bookingId: 'bk_123',
        resolved: true,
        resolvedBy: 'system',
        resolvedAt: '2024-01-20T13:50:00Z',
      },
      {
        id: '4',
        type: 'rate_limit_hit',
        severity: 'medium',
        timestamp: '2024-01-20T13:30:00Z',
        user: { id: 'u3', name: 'Emily Davis', role: 'renter', phone: '+234***5678' },
        details: {
          action: 'Share verification attempts',
          resource: 'Trip share verification',
          location: 'Ikeja, Lagos',
          ipAddress: '197.149.***.**',
          userAgent: 'Chrome/120.0 Mobile',
          attempts: 10,
          maxAttempts: 10,
        },
        resolved: true,
        resolvedBy: 'admin',
        resolvedAt: '2024-01-20T13:35:00Z',
      },
      {
        id: '5',
        type: 'unusual_location',
        severity: 'low',
        timestamp: '2024-01-20T12:00:00Z',
        user: { id: 'h1', name: 'David Wilson', role: 'host', phone: '+234***9012' },
        details: {
          action: 'Host verification access',
          resource: 'Host dashboard login',
          location: 'Abuja (unusual for this user)',
          ipAddress: '154.113.***.**',
          userAgent: 'PolarRide Mobile/2.1.0 (iOS 17.2)',
        },
        resolved: true,
        resolvedBy: 'user_verified',
        resolvedAt: '2024-01-20T12:05:00Z',
      },
    ];
    
    setSecurityEvents(mockEvents);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <ErrorIcon color="error" />;
      case 'high': return <WarningIcon color="warning" />;
      case 'medium': return <SecurityIcon color="info" />;
      case 'low': return <CheckIcon color="success" />;
      default: return <SecurityIcon />;
    }
  };

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'failed_verification': return 'Failed Verification';
      case 'suspicious_sharing': return 'Suspicious Sharing';
      case 'multiple_attempts': return 'Multiple Attempts';
      case 'unusual_location': return 'Unusual Location';
      case 'rate_limit_hit': return 'Rate Limit Hit';
      case 'expired_code_attempt': return 'Expired Code Attempt';
      default: return type;
    }
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'failed_verification': return <KeyIcon fontSize="small" />;
      case 'suspicious_sharing': return <ShareIcon fontSize="small" />;
      case 'multiple_attempts': return <BlockIcon fontSize="small" />;
      case 'unusual_location': return <LocationIcon fontSize="small" />;
      case 'rate_limit_hit': return <TimerIcon fontSize="small" />;
      case 'expired_code_attempt': return <KeyIcon fontSize="small" />;
      default: return <SecurityIcon fontSize="small" />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return format(date, 'MMM d, HH:mm');
  };

  const unresolvedEvents = securityEvents.filter(event => !event.resolved);
  const criticalUnresolved = unresolvedEvents.filter(event => event.severity === 'critical').length;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Security Monitoring
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Real-time monitoring of security events and verification activities
        </Typography>
      </Box>

      {/* Critical Alerts */}
      {criticalUnresolved > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{criticalUnresolved} Critical Security Event{criticalUnresolved > 1 ? 's' : ''} Require Immediate Attention</strong>
        </Alert>
      )}

      {/* Metrics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics.totalEvents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Events (24h)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {metrics.trends.eventsChange < 0 ? (
                    <TrendingDownIcon color="success" fontSize="small" />
                  ) : (
                    <TrendingUpIcon color="error" fontSize="small" />
                  )}
                  <Typography
                    variant="caption"
                    color={metrics.trends.eventsChange < 0 ? 'success.main' : 'error.main'}
                  >
                    {Math.abs(metrics.trends.eventsChange)}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="error">
                    {metrics.criticalEvents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Critical Events
                  </Typography>
                </Box>
                <ErrorIcon color="error" sx={{ fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {((metrics.resolvedEvents / metrics.totalEvents) * 100).toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Resolution Rate
                  </Typography>
                </Box>
                <CheckIcon color="success" sx={{ fontSize: 32 }} />
              </Box>
              <LinearProgress
                variant="determinate"
                value={(metrics.resolvedEvents / metrics.totalEvents) * 100}
                color="success"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {metrics.securityScore}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Security Score
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon color="success" fontSize="small" />
                  <Typography variant="caption" color="success.main">
                    +{metrics.trends.scoreChange}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Security Events */}
        <Grid item xs={12} md={8}>
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">
                Recent Security Events
              </Typography>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {securityEvents.map((event) => (
                    <TableRow 
                      key={event.id}
                      sx={{ 
                        backgroundColor: !event.resolved && event.severity === 'critical'
                          ? alpha(theme.palette.error.main, 0.05)
                          : 'transparent'
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getEventTypeIcon(event.type)}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {getEventTypeLabel(event.type)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getSeverityIcon(event.severity)}
                              <Chip 
                                label={event.severity} 
                                size="small" 
                                color={getSeverityColor(event.severity) as any}
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2">{event.user.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.user.role} • {event.user.phone}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2">{event.details.action}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {event.details.resource}
                          </Typography>
                          {event.details.location && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              📍 {event.details.location}
                            </Typography>
                          )}
                          {event.details.attempts && (
                            <Typography variant="caption" color="error.main" display="block">
                              {event.details.attempts}/{event.details.maxAttempts} attempts
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatTimeAgo(event.timestamp)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {event.resolved ? (
                          <Box>
                            <Chip 
                              label="Resolved" 
                              size="small" 
                              color="success"
                              icon={<CheckIcon />}
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                              by {event.resolvedBy}
                            </Typography>
                          </Box>
                        ) : (
                          <Chip 
                            label="Open" 
                            size="small" 
                            color="error"
                            icon={<WarningIcon />}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Security Summary */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            {/* Top Threats */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Security Threats
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <KeyIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Failed Verifications"
                        secondary="18 incidents (38%)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ShareIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Suspicious Sharing"
                        secondary="12 incidents (26%)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <BlockIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Multiple Attempts"
                        secondary="10 incidents (21%)"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <TimerIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Rate Limiting"
                        secondary="7 incidents (15%)"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* System Health */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    System Health
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <AssessmentIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Verification Services"
                        secondary="99.8% uptime"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ShieldIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Security Gateway"
                        secondary="All systems operational"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PhoneIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="SMS Service"
                        secondary="Minor delays (avg +2s)"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Stats */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Stats
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Avg Response Time</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metrics.averageResponseTime}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Events Today</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {metrics.totalEvents}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2">Unresolved</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {unresolvedEvents.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Security Score</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {metrics.securityScore}/100
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SecurityMonitoringPage;